import { otherColors } from '@ehrTheme/colors';
import { Autocomplete, Box, Card, Stack, TextField, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActionsList } from 'src/components/ActionsList';
import { DeleteIconButton } from 'src/components/DeleteIconButton';
import { RoundedButton } from 'src/components/RoundedButton';
import { dataTestIds } from 'src/constants/data-test-ids';
import { CPTCodeDTO } from 'utils';
import { useChartDataArrayValue } from '../../../hooks/useChartDataArrayValue';
import { useChartData } from '../../../stores/appointment/appointment.store';
import { ProviderSideListSkeleton } from '../../ProviderSideListSkeleton';

export const ProceduresForm: FC = () => {
  const { t } = useTranslation();

  const surgicalHistoryOptions: CPTCodeDTO[] = useMemo(
    () => [
      { display: t('telemed.chart.procedures.adenoidectomy'), code: '42830' },
      { display: t('telemed.chart.procedures.appendectomy'), code: '44950' },
      { display: t('telemed.chart.procedures.c_section'), code: '59510' },
      { display: t('telemed.chart.procedures.circumcision'), code: '54150' },
      { display: t('telemed.chart.procedures.cleft_lip'), code: '42200' },
      { display: t('telemed.chart.procedures.cyst_removed'), code: '97139' },
      { display: t('telemed.chart.procedures.dental_oral'), code: '41899' },
      { display: t('telemed.chart.procedures.ear_tube'), code: '69436' },
      { display: t('telemed.chart.procedures.elbow_hand_arm'), code: '24999' },
      { display: t('telemed.chart.procedures.feeding_tube'), code: '43246' },
      { display: t('telemed.chart.procedures.foot_ankle'), code: '27899' },
      { display: t('telemed.chart.procedures.frenotomy'), code: '41010' },
      { display: t('telemed.chart.procedures.gallbladder'), code: '47600' },
      { display: t('telemed.chart.procedures.heart_cardiac'), code: '33999' },
      { display: t('telemed.chart.procedures.hemangioma'), code: '17106' },
      { display: t('telemed.chart.procedures.hernia_repair'), code: '49617' },
      { display: t('telemed.chart.procedures.hydrocele'), code: '55060' },
      { display: t('telemed.chart.procedures.hypospadias'), code: '53450' },
      { display: t('telemed.chart.procedures.kidney'), code: '50540' },
      { display: t('telemed.chart.procedures.knee'), code: '29850' },
      { display: t('telemed.chart.procedures.orchiectomy'), code: '54520' },
      { display: t('telemed.chart.procedures.other_eye'), code: '65799' },
      { display: t('telemed.chart.procedures.pyloromyotomy'), code: '67599' },
      { display: t('telemed.chart.procedures.sinus'), code: '43520' },
      { display: t('telemed.chart.procedures.splenectomy'), code: '31299' },
      { display: t('telemed.chart.procedures.tear_duct'), code: '38100' },
      { display: t('telemed.chart.procedures.tons_adenoid'), code: '68899' },
      { display: t('telemed.chart.procedures.undescended_testicle'), code: '42820' },
      { display: t('telemed.chart.procedures.ventriculoperitoneal'), code: '54640' },
      { display: t('telemed.chart.procedures.wisdom_teeth'), code: '75809' },
      { display: t('telemed.chart.procedures.other'), code: '41899' },
    ],
    [t]
  );

  const methods = useForm<{
    selectedProcedure: CPTCodeDTO | null;
    otherProcedureName: string;
  }>({
    defaultValues: {
      selectedProcedure: null,
      otherProcedureName: '',
    },
  });
  const { isChartDataLoading } = useChartData();
  const [isOtherOptionSelected, setIsOtherOptionSelected] = useState(false);

  const { control, reset, handleSubmit } = methods;

  const { isLoading, onSubmit, onRemove, values: procedures } = useChartDataArrayValue('surgicalHistory', reset, {});

  const handleSelectOption = (data: CPTCodeDTO | null): void => {
    if (data) {
      void onSubmit(data);
      reset({ selectedProcedure: null, otherProcedureName: '' });
      setIsOtherOptionSelected(false);
    }
  };

  const onSubmitForm = (data: { selectedProcedure: CPTCodeDTO | null; otherProcedureName: string }): void => {
    if (data.selectedProcedure) {
      handleSelectOption({
        ...data.selectedProcedure,
        display: t('telemed.chart.procedures.other') + (data.otherProcedureName ? ` (${data.otherProcedureName})` : ''),
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: procedures.length || isChartDataLoading ? 2 : 0,
          }}
        >
          {isChartDataLoading ? (
            <ProviderSideListSkeleton />
          ) : (
            <Box data-testid={dataTestIds.surgicalHistory.surgicalHistoryList}>
              <ActionsList
                data={procedures}
                itemDataTestId={dataTestIds.surgicalHistory.surgicalHistoryListItem}
                getKey={(value) => value.resourceId!}
                renderItem={(value) => (
                  <Typography>
                    {value.code} {value.display}
                  </Typography>
                )}
                renderActions={(value) => (
                  <DeleteIconButton disabled={isLoading} onClick={() => onRemove(value.resourceId!)} />
                )}
                divider
              />
            </Box>
          )}
        </Box>
        <Card
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: otherColors.formCardBg,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Controller
            name="selectedProcedure"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                value={value || null}
                onChange={(_e, data) => {
                  onChange(data);
                  if (data?.display === t('telemed.chart.procedures.other')) {
                    setIsOtherOptionSelected(true);
                  } else {
                    setIsOtherOptionSelected(false);
                    handleSelectOption(data);
                  }
                }}
                fullWidth
                size="small"
                disabled={isLoading || isChartDataLoading}
                options={surgicalHistoryOptions}
                noOptionsText={t('telemed.chart.nothingFound')}
                getOptionLabel={(option) => `${option.code} ${option.display}`}
                renderOption={(props, option) => (
                  <li data-testid={dataTestIds.surgicalHistory.surgicalHistoryOption} {...props}>
                    <Typography component="span">
                      {option.code} {option.display}
                    </Typography>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('telemed.chart.surgeryLabel')}
                    placeholder={t('telemed.chart.searchPlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    data-testid={dataTestIds.surgicalHistory.surgicalHistoryInput}
                    sx={{
                      '& .MuiInputLabel-root': {
                        fontWeight: 'bold',
                      },
                    }}
                  />
                )}
              />
            )}
          />
          {isOtherOptionSelected && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Controller
                name="otherProcedureName"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    label={t('telemed.chart.otherSurgeryLabel')}
                    placeholder={t('telemed.chart.pleaseSpecify')}
                    fullWidth
                    size="small"
                  />
                )}
              />
              <RoundedButton type="submit">{t('telemed.vitals.add')}</RoundedButton>
            </Stack>
          )}
        </Card>
      </Box>
    </form>
  );
};
